import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Clock, User, Target, AlertTriangle } from 'lucide-react';

export function TodoGuide() {
  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">What is a To-Do in EOS?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-3 text-muted-foreground">
          <p>
            In the Entrepreneurial Operating System (EOS®), a <strong>To-Do</strong> is a short,
            specific action item committed to during the weekly Level 10 meeting. It's the
            smallest unit of accountability — a concrete promise made by one person to the team.
          </p>
          <p>
            A healthy team completes <strong>90% of its To-Dos on time</strong>. That number is the
            single best leading indicator of whether the team is actually executing on what it says.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">The four rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-3">
            <User className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <strong>One owner, always.</strong> Every To-Do belongs to exactly one person. If two
              people own it, nobody does. Shared accountability dilutes to zero.
            </div>
          </div>
          <div className="flex gap-3">
            <Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <strong>Seven days, not more.</strong> A To-Do is due by the next L10 — typically 7
              days out. If an action needs more than a week, it's not a To-Do; it's part of a Rock
              or an Issue.
            </div>
          </div>
          <div className="flex gap-3">
            <Target className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <strong>Specific and doable.</strong> "Fix marketing" is not a To-Do. "Send
              sponsorship proposal to Acme Corp by Friday" is. You should be able to tell at a
              glance whether it's done.
            </div>
          </div>
          <div className="flex gap-3">
            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <strong>Binary — done or not done.</strong> At the next L10, each To-Do gets one
              answer: yes or no. No "in progress," no "mostly done." If it's not complete, it
              either gets re-committed or it becomes an Issue to solve.
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">How To-Dos relate to Rocks and Issues</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-muted-foreground">
          <p>
            <strong>Rocks</strong> are 90-day priorities. A Rock is what you're working toward this
            quarter; a To-Do is a week-sized step that moves a Rock, an Issue, or a day-to-day
            commitment forward.
          </p>
          <p>
            <strong>Issues</strong> are things blocking the business that need to be Identified,
            Discussed, and Solved (IDS). Many Issues get solved by creating a To-Do. The To-Do is
            the action coming out of the conversation.
          </p>
          <p>
            When you finish an L10, your team should walk out with a short list of To-Dos —
            typically 3–7 total for the team — each owned by one person and due by next week.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> When a To-Do drops off
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            If a To-Do wasn't completed in 7 days, don't just push the date. Ask: why? If the
            answer reveals a real obstacle, drop the To-Do into the Issues list and solve the root
            cause. If it was simply missed, re-commit — but track the pattern. Repeated misses are
            an accountability conversation, not a scheduling one.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
